/**
 * The settings tab registry's pure rules (D-14, wp/settings §1.1): the contract a `settings-tabs.ts` file fulfils, the
 * collection of every contributing module's tabs, and the filter that decides which tabs a user sees — by software,
 * user type and read permission (FX-070, FX-027, FX-032; OPEN-2 (a)). Selection is by slug, never by index (NEW-1).
 */
import type { AclAction } from '@/app/acl'
import { EMPLOYER_SOFTWARES, type EmployerSoftware, type UserType } from '@/app/stores/session'
import type { SettingsTabDefinition } from '@/shared/settings/tabs'

// The contract lives in src/shared/settings/tabs.ts (queue #13); re-exported for this module's tabs.
export type { SettingsTabDefinition, SettingsTabProps } from '@/shared/settings/tabs'

/** A definition with the module folder that contributed it. */
export interface RegisteredTab extends SettingsTabDefinition {
  moduleId: string
}

/** `import.meta.glob('/src/modules/*\/settings-tabs.ts', { eager: true })`. */
export type TabModules = Record<string, { default?: unknown }>

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function moduleOf(file: string): string {
  return /\/modules\/([^/]+)\/settings-tabs\.ts$/.exec(file)?.[1] ?? file
}

function check(tab: Partial<SettingsTabDefinition>, file: string): asserts tab is SettingsTabDefinition {
  const problem =
    typeof tab.slug !== 'string' || !SLUG.test(tab.slug)
      ? 'slug'
      : typeof tab.label !== 'string' || !tab.label
        ? 'label'
        : typeof tab.icon !== 'string' || !tab.icon
          ? 'icon'
          : typeof tab.order !== 'number' || !Number.isFinite(tab.order)
            ? 'order'
            : !Array.isArray(tab.softwares) ||
                !tab.softwares.length ||
                !tab.softwares.every((software) => (EMPLOYER_SOFTWARES as readonly string[]).includes(software))
              ? 'softwares'
              : typeof tab.component !== 'function'
                ? 'component'
                : null
  if (problem) throw new Error(`${file}: settings tab ${JSON.stringify(tab.slug ?? '?')} has an invalid ${problem}`)
}

/**
 * Every contributed tab, checked and sorted by `order` (then slug). Throws on a malformed entry or a slug declared
 * twice — the same way the router refuses two modules declaring one route name.
 */
export function collectTabs(modules: TabModules): RegisteredTab[] {
  const owners = new Map<string, string>()
  const tabs: RegisteredTab[] = []
  for (const file of Object.keys(modules).sort()) {
    const declared = modules[file]?.default
    if (!Array.isArray(declared)) throw new Error(`${file} must export default an array of settings tabs`)
    const moduleId = moduleOf(file)
    for (const entry of declared as Partial<SettingsTabDefinition>[]) {
      check(entry, file)
      const owner = owners.get(entry.slug)
      if (owner)
        throw new Error(`Settings tab "${entry.slug}" is declared by both modules "${owner}" and "${moduleId}"`)
      owners.set(entry.slug, moduleId)
      tabs.push({ ...entry, moduleId })
    }
  }
  return tabs.sort((a, b) => a.order - b.order || a.slug.localeCompare(b.slug))
}

/** What the filter needs to know about the viewer. */
export interface TabViewer {
  software: string
  userType: UserType | null
  can: (resource: string, action: AclAction) => boolean
}

/** The tabs the viewer sees under the software, in order (a tab without its read permission is left out). */
export function availableTabs(tabs: readonly RegisteredTab[], viewer: TabViewer): RegisteredTab[] {
  return tabs.filter(
    (tab) =>
      (tab.softwares as readonly string[]).includes(viewer.software) &&
      (!tab.clientOnly || viewer.userType === 'Client') &&
      (!tab.read || viewer.can(tab.read, 'read')),
  )
}

/** The write resource of a tab under a software, or `null` when writing needs none. */
export function writeResource(tab: SettingsTabDefinition, software: EmployerSoftware): string | null {
  if (!tab.write) return null
  return typeof tab.write === 'function' ? tab.write(software) : tab.write
}

/** Whether the viewer may change the tab's data. */
export function canWriteTab(tab: SettingsTabDefinition, software: EmployerSoftware, viewer: TabViewer): boolean {
  const resource = writeResource(tab, software)
  return resource === null || viewer.can(resource, 'write')
}
